/**
 * FedEx-specific claim info collected from the merchant at claim creation
 * time. Includes dropdown enums + zod schemas for everything the FedEx
 * eClaims batch xlsx needs in its damage section (columns I, J, K, L, M).
 *
 * Single source of truth for:
 *   - UI dropdowns (render from the enum values)
 *   - Zod schemas stored on ShippingClaim.carrierSpecificInfo.fedex
 *   - xlsx file writer (enum values are emitted verbatim into the sheet)
 *
 * Values come from the template's "Data" tab and must match FedEx's
 * validator exactly — no prettifying.
 */
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// ---------------------------------------------------------------------------
// Dropdown enums
// ---------------------------------------------------------------------------

/** Column B — Claim Type */
export enum FedexClaimType {
  NOT_RECEIVED = "Shipment not received",
  MISSING_CONTENTS = "Shipment missing contents",
  DAMAGED = "Shipment damaged",
}
export const FedexClaimTypeSchema = createEnum(FedexClaimType, {
  enumName: "FedexClaimType",
}).meta({ privacy: privacy.nonPii });

/** Column F — Currency Type */
export enum FedexCurrency {
  USD = "U.S. Dollar (USD)",
  CAD = "CA Dollar (CAD)",
}
export const FedexCurrencySchema = createEnum(FedexCurrency, {
  enumName: "FedexCurrency",
}).meta({ privacy: privacy.nonPii });

/** Column G — Commodity */
export enum FedexCommodity {
  ANTIQUES = "Antiques, Collectibles, Jewelry (including coins, stamps, gold and silver)",
  CELL_PHONES = "Cell Phones, Tablets, Handheld Devices",
  COMPUTERS = "Computer Equipment, Software, Components, Accessories",
  DOCUMENTS = "Documents - Legal, Personal, Business (including visas, passports and IDs)",
  FOOD_AGRICULTURE = "Food, Animal, Agricultural, and Medical products, both perishable and non-perishable",
  FRAGILE = "Fragile & Glass Items (including bulbs, tubes, neon, fluorescents and scale models)",
  FINE_ART = "Furs, Fine Art, Paintings, Tapestry, Etched Glass",
  LIQUID = "Liquid Chemicals or Consumables (including wine, alcohol and cleaners)",
  MEDICINE = "Medicine, Drugs, Controlled Substances",
  MONEY = "Money, Currency, Tickets, Bonds or Equivalents",
  TOBACCO_FIREARMS = "Tobacco, Firearms",
  NONE_OF_THE_ABOVE = "None of the Above",
}
export const FedexCommoditySchema = createEnum(FedexCommodity, {
  enumName: "FedexCommodity",
}).meta({ privacy: privacy.nonPii });

/** Column I — Type of Damage to Contents */
export enum FedexDamageToContents {
  NONE = "None",
  BENT = "Bent",
  BROKEN = "Contents Broken",
  MISSING = "Contents Missing",
  PROTRUDING = "Contents Protruding",
}
export const FedexDamageToContentsSchema = createEnum(FedexDamageToContents, {
  enumName: "FedexDamageToContents",
}).meta({ privacy: privacy.nonPii });

/**
 * Column J — Type of Damage to Outer Packaging.
 * Note the trailing space after "Closures Open " — that's how the template
 * ships it. Must match or FedEx rejects the cell.
 */
export enum FedexDamageToOuter {
  CLOSURES_OPEN = "Closures Open ",
  CORNER_CRUSHED = "Corner of Box Crushed",
  CRUSHED = "Crushed",
  NO_VISIBLE_DAMAGE = "No Visible Damage",
  PUNCTURED = "Punctured",
  RIPPED = "Ripped/Torn",
  RETAPED = "Retaped",
  WET = "Wet",
  OTHER = "Other, please specify",
}
export const FedexDamageToOuterSchema = createEnum(FedexDamageToOuter, {
  enumName: "FedexDamageToOuter",
}).meta({ privacy: privacy.nonPii });

/** Column L — Packaging Materials */
export enum FedexPackagingMaterial {
  NONE = "None",
  BUBBLE_WRAP = "Bubble Wrap",
  CORNER_POST = "Corner Post",
  CORRUGATED_LINERS = "Corrugated Liners",
  MOLDED_FOAM = "Molded Foam",
  PLASTIC_BAGS = "Plastic Bags",
  PAPER = "Paper",
  SHREDDED_PAPER = "Shredded Paper",
  STYROFOAM_PEANUTS = "Styrofoam Peanuts",
  OTHER = "Other, please specify",
}
export const FedexPackagingMaterialSchema = createEnum(FedexPackagingMaterial, {
  enumName: "FedexPackagingMaterial",
}).meta({ privacy: privacy.nonPii });

// ---------------------------------------------------------------------------
// Damage section inputs (columns I, J, K, L, M)
// ---------------------------------------------------------------------------

export const FedexDamageInfoSchema = z.object({
  /** I — Type of damage to contents */
  damageToContents: FedexDamageToContentsSchema.meta({
    privacy: privacy.nonPii,
  }),
  /** J — Type of damage to outer packaging */
  damageToOuter: FedexDamageToOuterSchema.meta({ privacy: privacy.nonPii }),
  /** K — Other (Damage to Outer Packaging), ≤66 chars, only when J=OTHER */
  damageToOuterOther: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
  /** L — Packaging materials */
  packagingMaterials: FedexPackagingMaterialSchema.meta({
    privacy: privacy.nonPii,
  }),
  /** M — Other (Packaging Materials), ≤66 chars, only when L=OTHER */
  packagingMaterialsOther: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
});
export type FedexDamageInfo = z.infer<typeof FedexDamageInfoSchema>;

export function missingFedexDamageFields(
  d: Partial<FedexDamageInfo>,
): (keyof FedexDamageInfo)[] {
  const required: (keyof FedexDamageInfo)[] = [
    "damageToContents",
    "damageToOuter",
    "packagingMaterials",
  ];
  if (d.damageToOuter === FedexDamageToOuter.OTHER) {
    required.push("damageToOuterOther");
  }
  if (d.packagingMaterials === FedexPackagingMaterial.OTHER) {
    required.push("packagingMaterialsOther");
  }
  return required.filter((key) => !d[key]);
}

export function isFedexDamageInfoComplete(
  d: Partial<FedexDamageInfo>,
): d is FedexDamageInfo {
  return missingFedexDamageFields(d).length === 0;
}

export const FedexClaimInfoSchema = z.object({
  damage: FedexDamageInfoSchema.nullish().meta({ privacy: privacy.structured }),
});
export type FedexClaimInfo = z.infer<typeof FedexClaimInfoSchema>;
